import { useEffect, useMemo, useRef, useState } from "react";
import SystemViewport from "../components/SystemViewport";
import SystemDiagnostics from "../components/SystemDiagnostics";
import useAxiosPrivate from "../utils/useAxiosPrivate";
import { useAuth } from "../utils/AuthProvider";
import { canViewInternalDetails } from "../utils/permissions";
import { useContactDataHub } from "../utils/SignalR";
import i18n from "../translationUtils/i18n";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import BatteryFullRoundedIcon from "@mui/icons-material/BatteryFullRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import EmergencyShareRoundedIcon from "@mui/icons-material/EmergencyShareRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import LanRoundedIcon from "@mui/icons-material/LanRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import NotificationImportantRoundedIcon from "@mui/icons-material/NotificationImportantRounded";
import PowerRoundedIcon from "@mui/icons-material/PowerRounded";
import SearchIcon from "@mui/icons-material/Search";
import SensorsRoundedIcon from "@mui/icons-material/SensorsRounded";
import SmartphoneRoundedIcon from "@mui/icons-material/SmartphoneRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ThermostatRoundedIcon from "@mui/icons-material/ThermostatRounded";
import HvacRoundedIcon from "@mui/icons-material/HvacRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import { parseUtc } from "../utils/utcDate";

// 10 signals tracked per system (fixed order):
//   1. Monitoring Connection      — network.stationConnection
//   2. Primary Network            — network.primaryInstalled
//   3. Cellular Status            — network.failoverActive
//   4. AC Power                   — !powerFail
//   5. Backup Battery             — !upS1.batteryPowerActive
//   6. Suppressant Level          — tankLevel
//   7. Nitrogen Level             — highSidePressure + lowSidePressure
//   8. Thermal Detector Connection — rollup over arrangement1-4.leftThermal/rightThermal
//   9. Flame Detector Connection   — rollup over arrangement1-4.leftOFD/rightOFD (incl. faultActive)
//  10. Emergency Button          — mainE_Button + arrangement1-4.e_Button (voltage, disconnected, shorted)
//
// Readings without a signal of their own fold into the closest one rather than
// adding a key: low-side pressure → nitrogen, enclosure temperature → climate.
//
// Every threshold lives in SystemHealthService — this page never compares a raw
// reading. The Diagnostics tab colours raw values using the bands the payload
// carries (healthData.thresholds).
//
// Severity order (low → high): ok < unknown < warn < fault
// When Monitoring Connection is down, signals 2-10 should cascade to 'unknown'.

const SIGNAL_SEVERITY_TO_MUI = { warn: "warning", fault: "error" };
const SEVERITY_RANK = { fault: 3, warn: 2, unknown: 1, ok: 0 };

function timeAgo(iso) {
  const at = parseUtc(iso);
  if (!at) return "";
  const ms = Date.now() - at.getTime();
  const s = Math.max(1, Math.floor(ms / 1000));
  if (s < 60) return i18n.t("ago_seconds", { count: s });
  const m = Math.floor(s / 60);
  if (m < 60) return i18n.t("ago_minutes", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return i18n.t("ago_hours", { count: h });
  return i18n.t("ago_days", { count: Math.floor(h / 24) });
}

function sortAlerts(alerts) {
  return [...alerts].sort((a, b) => {
    const sev = SEVERITY_RANK[b.faultType] - SEVERITY_RANK[a.faultType];
    if (sev !== 0) return sev;
    return new Date(b.timestamp) - new Date(a.timestamp);
  });
}

// Temporarily suppress alerts for signals whose telemetry isn't in the DB yet
// (see HIDDEN_SIGNALS). A single alert's faultMessages can bundle several faults,
// so we strip only the matching messages and keep the rest of the alert; an alert
// is dropped entirely only when every one of its messages was suppressed. Matching
// is case-insensitive substring. Empty this array to stop suppressing.
const SUPPRESSED_ALERT_PATTERNS = [/primary network/i];

function filterSuppressedAlerts(alerts) {
  if (SUPPRESSED_ALERT_PATTERNS.length === 0) return alerts;
  const result = [];
  for (const alert of alerts) {
    const messages = alert.faultMessages ?? [];
    // Keep alerts that carry no messages untouched — there's nothing to match.
    if (messages.length === 0) {
      result.push(alert);
      continue;
    }
    const kept = messages.filter(
      (m) => !SUPPRESSED_ALERT_PATTERNS.some((re) => re.test(m)),
    );
    if (kept.length === 0) continue; // whole alert was suppressed-signal-only
    result.push({ ...alert, faultMessages: kept });
  }
  return result;
}

const TOP_ALERTS_COUNT = 5;
const ALERTS_PAGE_SIZE = 20;

// ── Sub-components ──────────────────────────────────────────────────────────

function EventCard({ alert, variant, onClick }) {
  const messages = alert.faultMessages ?? [];
  const note = messages[0] || i18n.t("needsattention");
  const location = [alert.site?.siteName, alert.site?.siteState]
    .filter(Boolean)
    .join(", ");

  return (
    <Alert
      severity={SIGNAL_SEVERITY_TO_MUI[alert.faultType]}
      variant={variant}
      sx={{
        "& .MuiAlert-action": { alignItems: "center", pt: 0, mt: 0 },
      }}
      action={
        <Button color="inherit" size="small" onClick={() => onClick?.(alert)}>
          DETAILS
        </Button>
      }
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
        <Chip
          label={alert.system?.systemType}
          color="secondary"
          size="small"
          sx={{ fontWeight: 700 }}
        />
        <Typography variant="body1" fontWeight={700}>
          {alert.system?.systemName}
        </Typography>
      </Stack>
      <Typography variant="body2">
        {location} · {note} · {timeAgo(alert.timestamp)}
      </Typography>
    </Alert>
  );
}

function EventRow({ alert, onClick }) {
  const messages = alert.faultMessages ?? [];
  const Icon =
    alert.faultType === "fault" ? ErrorRoundedIcon : WarningAmberRoundedIcon;
  const iconColor = alert.faultType === "fault" ? "error" : "warning";
  const subtitle =
    messages.length > 1
      ? `${messages[0]} +${messages.length - 1} more`
      : messages[0] || "";

  return (
    <ListItemButton divider onClick={() => onClick?.(alert)} sx={{ py: 1 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ width: "100%" }}
      >
        <Icon color={iconColor} fontSize="small" />
        <Chip label={alert.system?.systemType} color="secondary" size="small" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {alert.system?.systemName}
            </Box>{" "}
            · {alert.site?.siteName}, {alert.site?.siteState}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {subtitle} · {timeAgo(alert.timestamp)}
          </Typography>
        </Box>
      </Stack>
    </ListItemButton>
  );
}

function AllAlertsDrawer({ open, onClose, alerts, onAlertClick }) {
  const [visibleCount, setVisibleCount] = useState(ALERTS_PAGE_SIZE);

  useEffect(() => {
    if (open) setVisibleCount(ALERTS_PAGE_SIZE);
  }, [open]);

  const visibleAlerts = alerts.slice(0, visibleCount);
  const remaining = alerts.length - visibleAlerts.length;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: 420 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            {i18n.t("alerts")}
          </Typography>
          <IconButton onClick={onClose} aria-label={i18n.t("closealertsdrawer")}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <List sx={{ overflowY: "auto", flex: 1, minHeight: 0, py: 0 }}>
          {visibleAlerts.map((alert, idx) => (
            <EventRow
              key={`${alert.system?.productId}-${idx}`}
              alert={alert}
              onClick={onAlertClick}
            />
          ))}
          {remaining > 0 && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setVisibleCount((count) => count + ALERTS_PAGE_SIZE)
                }
              >
                Show More ({remaining})
              </Button>
            </Box>
          )}
        </List>
      </Box>
    </Drawer>
  );
}

function EmptyState({ totalSites, lastUpdated }) {
  return (
    <Alert
      severity="success"
      variant="outlined"
      icon={<CheckCircleRoundedIcon />}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>{i18n.t("allsystemsnormal")}</AlertTitle>
      Monitoring {totalSites} site{totalSites !== 1 ? "s" : ""} · last update{" "}
      {lastUpdated}
    </Alert>
  );
}

function AlertsLoadingState() {
  return (
    <Stack spacing={2} aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} variant="rounded" height={88} animation="wave" />
      ))}
    </Stack>
  );
}

function AlertsErrorState({ error, onRetry }) {
  const status = error?.response?.status;
  const detail = status
    ? i18n
        .t("serverresponded", {
          status,
          text: error.response.statusText || "",
        })
        .trim()
    : error?.message || i18n.t("networkerror");

  return (
    <Alert
      severity="error"
      variant="outlined"
      sx={{
        "& .MuiAlert-action": { alignItems: "center", pt: 0, mt: 0 },
      }}
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry}>
            RETRY
          </Button>
        )
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>{i18n.t("unabletoloadalerts")}</AlertTitle>
      {detail}
    </Alert>
  );
}

function HealthAlerts({
  alerts,
  loading,
  error,
  totalSites,
  lastUpdated,
  onAlertClick,
  onRetry,
}) {
  const theme = useTheme();
  const variant = theme.palette.mode === "dark" ? "outlined" : "standard";
  const sorted = useMemo(() => sortAlerts(alerts ?? []), [alerts]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) {
    return <AlertsLoadingState />;
  }

  if (error) {
    return <AlertsErrorState error={error} onRetry={onRetry} />;
  }

  if (sorted.length === 0) {
    return <EmptyState totalSites={totalSites} lastUpdated={lastUpdated} />;
  }

  const top = sorted.slice(0, TOP_ALERTS_COUNT);
  const remaining = sorted.length - top.length;

  return (
    <>
      <Stack spacing={2}>
        {top.map((alert, idx) => (
          <EventCard
            key={`${alert.system?.productId}-${idx}`}
            alert={alert}
            variant={variant}
            onClick={onAlertClick}
          />
        ))}
        {remaining > 0 && (
          <Divider textAlign="center">
            <Chip
              label={`${i18n.t("viewallalerts")} (${sorted.length})`}
              variant="outlined"
              size="small"
              clickable
              onClick={() => setDrawerOpen(true)}
            />
          </Divider>
        )}
      </Stack>
      <AllAlertsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        alerts={sorted}
        onAlertClick={onAlertClick}
      />
    </>
  );
}

// ── Sites & Systems lookup ─────────────────────────────────────────────────

const SIGNAL_ORDER = [
  "monitoring",
  "primary",
  "cellular",
  "ac",
  "battery",
  "suppressant",
  "nitrogen",
  "thermal",
  "flame",
  "supervisory",
  "ebutton",
];

const SIGNALS_BY_TYPE = {
  BSU: SIGNAL_ORDER, // all 11
  CF: ["monitoring", "primary", "cellular", "ac", "battery", "thermal", "flame", "supervisory", "ebutton"],
  ED: ["monitoring", "primary", "cellular", "ac", "battery", "thermal", "flame", "supervisory", "ebutton"],
  MSU: ["monitoring", "primary", "cellular", "ac", "battery", "thermal", "flame", "supervisory", "ebutton"],
  OW: ["monitoring", "cellular", "battery", "thermal", "flame"],
};

// Temporarily hidden: Primary Network telemetry is not yet being written to the
// DB. This covers both "primary" (network.primaryInstalled) and "monitoring"
// (network.stationConnection, shown as "Monitoring Connection"). Removing a key
// here restores it everywhere at once — the overview dots, the detail modal
// list, and the status roll-up counts — without deleting any of that code.
const HIDDEN_SIGNALS = new Set(["primary", "monitoring"]);

// Enclosure climate is only wired up on FR hardware, so the signal is gated on
// the unit name rather than the system type. Mirrors IsClimateMonitored() in
// SystemHealthService — the backend omits the signal entirely for other units.
function isClimateMonitored(systemName) {
  return /^\s*FR\d/i.test(systemName ?? "");
}

function signalsForSystemType(systemType, systemName) {
  const base = SIGNALS_BY_TYPE[systemType] ?? SIGNAL_ORDER;
  const keys = isClimateMonitored(systemName) ? [...base, "climate"] : base;
  return keys.filter((key) => !HIDDEN_SIGNALS.has(key));
}

// Resolved per render rather than held in a module-level map, so a locale
// change takes effect without a reload.
function signalLabel(signalKey) {
  return i18n.t(`sig_${signalKey}`);
}

function stateText(state) {
  return i18n.t(`state_${state}`);
}

const STATE_COLOR = {
  ok: "success.main",
  warn: "warning.main",
  fault: "error.main",
  unknown: "grey.400",
};

const SIGNAL_ICON = {
  monitoring: SensorsRoundedIcon,
  primary: LanRoundedIcon,
  cellular: SmartphoneRoundedIcon,
  ac: PowerRoundedIcon,
  battery: BatteryFullRoundedIcon,
  suppressant: WaterDropRoundedIcon,
  nitrogen: SpeedRoundedIcon,
  thermal: ThermostatRoundedIcon,
  flame: LocalFireDepartmentRoundedIcon,
  climate: HvacRoundedIcon,
  supervisory: NotificationImportantRoundedIcon,
  ebutton: EmergencyShareRoundedIcon,
};

const SEARCH_THRESHOLD = 5;

// ── Telemetry generation ────────────────────────────────────────────────────
//
// Only GEN2 hardware reports live telemetry. Everything else is a
// first-generation product that never produces readings, so instead of the
// "No reading yet" + greyed-out signal dots we show an explanatory message.
// Generation is derived from the systemName's letter prefix + number:
//   FR545–FR547 and FR580+   → GEN2
//   CF149+                   → GEN2
//   T180, T181, T189+        → GEN2
//   anything else            → GEN1
const GEN2_BY_PREFIX = {
  FR: (n) => (n >= 545 && n <= 547) || n >= 580,
  CF: (n) => n >= 149,
  T: (n) => n === 180 || n === 181 || n >= 189,
};


function isFirstGenSystem(systemName) {
  const match = /^\s*([A-Za-z]+)[-\s]*(\d+)/.exec(systemName ?? "");
  if (!match) return true;
  const prefix = match[1].toUpperCase();
  return !(GEN2_BY_PREFIX[prefix]?.(parseInt(match[2], 10)) ?? false);
}


function SiteStatusIcon({ state, size = "small" }) {
  if (state === "fault")
    return <ErrorRoundedIcon color="error" fontSize={size} />;
  if (state === "warn")
    return <WarningAmberRoundedIcon color="warning" fontSize={size} />;
  if (state === "unknown")
    return <HelpOutlineRoundedIcon color="disabled" fontSize={size} />;
  return <CheckCircleRoundedIcon color="success" fontSize={size} />;
}

function alertBelongsToSite(alert, site) {
  const aSite = alert.site;
  if (!aSite) return false;
  if (aSite.csId != null && site.csId != null) return aSite.csId === site.csId;
  const an = (aSite.siteName ?? "").trim().toLowerCase();
  const sn = (site.csName ?? "").trim().toLowerCase();
  return an !== "" && an === sn;
}

const HOVER_CARD_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 320,
      p: 0.5,
      bgcolor: "background.paper",
      color: "text.primary",
      boxShadow: 3,
      border: 1,
      borderColor: "divider",
    },
  },
};

function SiteAlertsHover({ alerts, onAlertClick, children }) {
  const content = (
    <Stack sx={{ py: 0.5 }}>
      {sortAlerts(alerts).map((alert, idx) => {
        const isFault = alert.faultType === "fault";
        const Icon = isFault ? ErrorRoundedIcon : WarningAmberRoundedIcon;
        const messages = alert.faultMessages ?? [];
        const note =
          messages.length > 1
            ? `${messages[0]} ${i18n.t("plusmore", { count: messages.length - 1 })}`
            : messages[0] || i18n.t("needsattention");
        return (
          <Stack
            key={`${alert.system?.productId}-${idx}`}
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={(e) => {
              e.stopPropagation();
              onAlertClick?.(alert);
            }}
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: 1,
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Icon color={isFault ? "error" : "warning"} fontSize="small" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {alert.system?.systemName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {note}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
  return (
    <Tooltip
      title={content}
      placement="right"
      arrow
      componentsProps={HOVER_CARD_TOOLTIP_PROPS}
    >
      {children}
    </Tooltip>
  );
}

const SITE_STATUS_INFO = {
  ok: {
    Icon: CheckCircleRoundedIcon,
    color: "success",
    titleKey: "allsystemsnormal",
    detailKey: "nowarningsorfaults",
  },
  unknown: {
    Icon: HelpOutlineRoundedIcon,
    color: "disabled",
    titleKey: "nocurrentreadings",
    detailKey: "sitenotreportingtelemetry",
  },
};

function SiteStatusInfoHover({ state, children }) {
  const cfg = SITE_STATUS_INFO[state];
  if (!cfg) return children;
  const { Icon } = cfg;
  const content = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 1, py: 0.75, maxWidth: 260 }}
    >
      <Icon color={cfg.color} fontSize="small" />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700}>
          {i18n.t(cfg.titleKey)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {i18n.t(cfg.detailKey)}
        </Typography>
      </Box>
    </Stack>
  );
  return (
    <Tooltip
      title={content}
      placement="right"
      arrow
      componentsProps={HOVER_CARD_TOOLTIP_PROPS}
    >
      {children}
    </Tooltip>
  );
}

function SignalDot({ signalKey, state }) {
  const tooltip = `${signalLabel(signalKey)} — ${stateText(state)}`;
  const Icon = SIGNAL_ICON[signalKey];
  const isUnknown = state === "unknown";
  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Avatar
        role="img"
        aria-label={tooltip}
        sx={{
          width: 24,
          height: 24,
          bgcolor: isUnknown ? "transparent" : STATE_COLOR[state],
          color: isUnknown ? "grey.500" : "common.white",
          border: isUnknown ? 1 : 0,
          borderColor: "grey.400",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Avatar>
    </Tooltip>
  );
}

function SignalStrip({ signals, systemType, systemName }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {signalsForSystemType(systemType, systemName).map((key) => (
        <SignalDot
          key={key}
          signalKey={key}
          state={signals?.[key] || "unknown"}
        />
      ))}
    </Stack>
  );
}

function SystemRow({ system, onClick }) {
  const noTelemetry = isFirstGenSystem(system.systemName);
  const isStale = !noTelemetry && !!system.stale;
  const inactive = noTelemetry || isStale;
  const summary =
    noTelemetry || isStale
      ? null
      : systemStatusSummary(system.signals, system.systemType, system.systemName);

  const StatusIcon = inactive
    ? HelpOutlineRoundedIcon
    : summary.state === "fault"
      ? ErrorRoundedIcon
      : summary.state === "warn"
        ? WarningAmberRoundedIcon
        : CheckCircleRoundedIcon;
  const iconColor = inactive
    ? "disabled"
    : summary.state === "fault"
      ? "error"
      : summary.state === "warn"
        ? "warning"
        : "success";
  const textColor = inactive
    ? "text.secondary"
    : summary.state === "fault"
      ? "error.main"
      : summary.state === "warn"
        ? "warning.main"
        : "success.main";

  const statusText = noTelemetry
    ? i18n.t("telemetryunavailablefirstgen")
    : isStale
      ? system.lastUpdated
        ? i18n.t("stalelastreading", { time: timeAgo(system.lastUpdated) })
        : i18n.t("noreadingyet")
      : summary.text;

  return (
    <Box
      onClick={inactive ? undefined : onClick}
      sx={{
        py: 1.25,
        px: 1,
        mx: -1,
        borderRadius: 1,
        cursor: inactive ? "default" : "pointer",
        opacity: inactive ? 0.6 : 1,
        "&:hover": inactive ? {} : { bgcolor: "action.hover" },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Chip
          label={system.systemType}
          color="secondary"
          sx={{ flexShrink: 0, fontWeight: 700 }}
        />
        <Typography
          variant="body1"
          fontWeight={700}
          sx={{ flex: 1, minWidth: 0 }}
          noWrap
        >
          {system.systemName}
        </Typography>
        {noTelemetry ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              flexShrink: 0,
              maxWidth: { xs: 180, sm: 260 },
              textAlign: "right",
            }}
          >
            {statusText}
          </Typography>
        ) : (
          <>
            <SignalStrip
              signals={isStale ? null : system.signals}
              systemType={system.systemType}
              systemName={system.systemName}
            />
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ minWidth: 200, justifyContent: "flex-end" }}
              >
                <StatusIcon color={iconColor} fontSize="small" />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: textColor }}
                  noWrap
                >
                  {statusText}
                </Typography>
              </Stack>
              <ChevronRightRoundedIcon
                fontSize="small"
                sx={{
                  color: "text.disabled",
                  flexShrink: 0,
                  visibility: isStale ? "hidden" : "visible",
                }}
              />
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}

function systemStatusSummary(signals, systemType, systemName) {
  if (!signals) return { state: "unknown", text: i18n.t("loading") };
  const keys = signalsForSystemType(systemType, systemName);
  const faults = keys.filter((k) => signals[k] === "fault").length;
  const warns = keys.filter((k) => signals[k] === "warn").length;
  if (faults > 0) {
    return {
      state: "fault",
      text:
        faults === 1
          ? i18n.t("faultneedsattention")
          : i18n.t("faultsneedattention", { count: faults }),
    };
  }
  if (warns > 0) {
    return {
      state: "warn",
      text:
        warns === 1
          ? i18n.t("onewarning")
          : i18n.t("warningscount", { count: warns }),
    };
  }
  return { state: "ok", text: i18n.t("allsystemsok") };
}


function SystemDrawerRow({ system, onClick }) {
  const noTelemetry = isFirstGenSystem(system.systemName);
  const isStale = !noTelemetry && !!system.stale;
  const inactive = noTelemetry || isStale;
  const staleText = system.lastUpdated
    ? i18n.t("stalelastreading", { time: timeAgo(system.lastUpdated) })
    : i18n.t("noreadingyet");
  return (
    <Box
      onClick={inactive ? undefined : onClick}
      sx={{
        px: 2,
        py: 1.5,
        cursor: inactive ? "default" : "pointer",
        opacity: inactive ? 0.6 : 1,
        borderBottom: 1,
        borderColor: "divider",
        "&:hover": inactive ? {} : { bgcolor: "action.hover" },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            label={system.systemType}
            color="secondary"
            size="small"
            sx={{ flexShrink: 0 }}
          />
          <Typography variant="body2" fontWeight={600} noWrap>
            {system.systemName}
          </Typography>
        </Stack>
        {noTelemetry ? (
          <Typography variant="caption" color="text.secondary">
            {i18n.t("telemetryunavailablefirstgen")}
          </Typography>
        ) : (
          <>
            <SignalStrip
              signals={isStale ? null : system.signals}
              systemType={system.systemType}
              systemName={system.systemName}
            />
            {isStale && (
              <Typography variant="caption" color="text.secondary">
                {staleText}
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}

function SystemsDrawer({ open, onClose, site, onSystemClick }) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: 440 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">{i18n.t("systems")}</Typography>
            {site && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {site.csName}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label={i18n.t("closesystemsdrawer")}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {site?.systems?.map((system) => (
            <SystemDrawerRow
              key={system.productId ?? system.systemName}
              system={system}
              onClick={() => onSystemClick?.(system, site)}
            />
          ))}
        </Box>
      </Box>
    </Drawer>
  );
}

const INLINE_SYSTEMS_LIMIT = 5;

function SiteAccordion({
  site,
  expanded,
  onToggle,
  onSystemClick,
  onShowAllSystems,
  refreshKey,
  siteAlerts = [],
  onAlertClick,
}) {
  const axiosPrivate = useAxiosPrivate();
  const [systems, setSystems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localRetry, setLocalRetry] = useState(0);

  useEffect(() => {
    if (!expanded) return undefined;
    const controller = new AbortController();
    const silent = systems !== null;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    axiosPrivate
      .get(`/api/sites/${site.csId}/systems`, { signal: controller.signal })
      .then((r) => setSystems(r.data?.systems ?? []))
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        if (!silent) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted && !silent) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line
  }, [expanded, site.csId, refreshKey, localRetry]);

  const systemCount = site.systemCount ?? 0;
  const rollup = rollupFromAlerts(siteAlerts, site.rollupStatus);
  const address = [site.csAddress1, site.csCity, site.csState]
    .filter(Boolean)
    .join(", ");

  const hasMore = (systems?.length ?? 0) > INLINE_SYSTEMS_LIMIT;
  const visibleSystems = systems
    ? hasMore
      ? systems.slice(0, INLINE_SYSTEMS_LIMIT)
      : systems
    : [];

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      onChange={() => onToggle(site.csId)}
      sx={{
        "&:before": { display: "none" },
        "&:not(:last-of-type)": { borderBottom: 1, borderColor: "divider" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ width: "100%", minWidth: 0 }}
        >
          {siteAlerts.length > 0 ? (
            <SiteAlertsHover alerts={siteAlerts} onAlertClick={onAlertClick}>
              <Box
                component="span"
                sx={{ display: "inline-flex", cursor: "pointer" }}
                onClick={(e) => e.stopPropagation()}
              >
                <SiteStatusIcon state={rollup} />
              </Box>
            </SiteAlertsHover>
          ) : SITE_STATUS_INFO[rollup] ? (
            <SiteStatusInfoHover state={rollup}>
              <Box component="span" sx={{ display: "inline-flex" }}>
                <SiteStatusIcon state={rollup} />
              </Box>
            </SiteStatusInfoHover>
          ) : (
            <SiteStatusIcon state={rollup} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {site.csName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {address}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              {systemCount}
            </Box>{" "}
            system{systemCount !== 1 ? "s" : ""}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          bgcolor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          px: 2,
          pt: 1,
          pb: 2,
        }}
      >
        {error ? (
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              "& .MuiAlert-action": { alignItems: "center", pt: 0, mt: 0 },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setLocalRetry((n) => n + 1)}
              >
                RETRY
              </Button>
            }
          >
            <AlertTitle sx={{ fontWeight: 700 }}>
              {i18n.t("unabletoloadsystems")}
            </AlertTitle>
            {error.response?.status
              ? i18n
                  .t("serverresponded", {
                    status: error.response.status,
                    text: error.response.statusText || "",
                  })
                  .trim()
              : error.message || i18n.t("networkerror")}
          </Alert>
        ) : loading || !systems ? (
          <Stack spacing={1}>
            {Array.from({ length: Math.min(systemCount || 3, 5) }).map(
              (_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={56}
                  animation="wave"
                />
              ),
            )}
          </Stack>
        ) : (
          <>
            <Stack divider={<Divider flexItem />}>
              {visibleSystems.map((system) => (
                <SystemRow
                  key={system.productId ?? system.systemName}
                  system={system}
                  onClick={() => onSystemClick?.(system, site)}
                />
              ))}
            </Stack>
            {hasMore && (
              <Box sx={{ pt: 2, textAlign: "center" }}>
                <Chip
                  label={i18n.t("viewallsystems", { count: systems.length })}
                  variant="outlined"
                  size="small"
                  clickable
                  onClick={() => onShowAllSystems?.(site, systems)}
                />
              </Box>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}


function mergeSitesStable(prev, incoming) {
  const incomingById = new Map(incoming.map((s) => [s.csId, s]));
  const seen = new Set(prev.map((s) => s.csId));
  const merged = prev.map((site) => incomingById.get(site.csId) ?? site);
  for (const site of incoming) {
    if (!seen.has(site.csId)) merged.push(site);
  }
  return merged;
}

function rollupFromAlerts(siteAlerts, rollupStatus) {
  if (siteAlerts.some((a) => a.faultType === "fault")) return "fault";
  if (siteAlerts.some((a) => a.faultType === "warn")) return "warn";
  return (rollupStatus || "unknown") === "unknown" ? "unknown" : "ok";
}

function SiteLookup({
  onSystemClick,
  onAlertClick,
  alerts = [],
  sitesRefreshKey,
  systemsRefreshKey,
  lastUpdateAt,
}) {
  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [sites, setSites] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalSystems, setTotalSystems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [expandedIds, setExpandedIds] = useState(new Set());
  const [drawerState, setDrawerState] = useState(null);
  const [hideNoReadings, setHideNoReadings] = useState(false);
  const prevQueryRef = useRef(null);
  const orderRankRef = useRef(new Map());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    const q = debouncedSearch.trim();
    const query = `${q}|${hideNoReadings}`;
    const isFresh = prevQueryRef.current !== query;
    if (isFresh) {
      setLoading(true);
      setError(null);
    }
    const params = new URLSearchParams({ page: "1", pageSize: "100" });
    if (q) params.set("search", q);
    if (hideNoReadings) params.set("excludeUnknown", "true");
    axiosPrivate
      .get(`/api/sites/${cookies.user}?${params.toString()}`, {
        signal: controller.signal,
      })
      .then((r) => {
        const incoming = r.data?.sites ?? [];
        setTotalCount(r.data?.totalCount ?? 0);
        prevQueryRef.current = query;
        if (isFresh) orderRankRef.current = new Map();
        setSites((prev) => (isFresh ? incoming : mergeSitesStable(prev, incoming)));
        setError(null);
        setTotalSystems(r.data?.totalSystems ?? 0);
      })
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        if (isFresh) setError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted && isFresh) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line
  }, [debouncedSearch, hideNoReadings, sitesRefreshKey]);

  useEffect(() => {
    if (debouncedSearch.trim()) {
      setExpandedIds(new Set(sites.map((s) => s.csId)));
    }
  }, [debouncedSearch, sites]);

  const toggleExpanded = (csId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(csId)) next.delete(csId);
      else next.add(csId);
      return next;
    });
  };

  const showSearch =
    totalCount > SEARCH_THRESHOLD || debouncedSearch.trim().length > 0;

  const searchPending = searchInput !== debouncedSearch;
  const searching = searchPending || loading;

  const orderedSites = useMemo(() => {
    const ranks = orderRankRef.current;
    const decorated = sites.map((site, index) => {
      const state = rollupFromAlerts(
        alerts.filter((a) => alertBelongsToSite(a, site)),
        site.rollupStatus,
      );
      const current = SEVERITY_RANK[state];
      const prev = ranks.get(site.csId);
      const rank = prev == null ? current : Math.max(prev, current);
      ranks.set(site.csId, rank);
      return { site, index, rank };
    });
    return decorated
      .sort((a, b) => b.rank - a.rank || a.index - b.index)
      .map((d) => d.site);
  }, [sites, alerts]);

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexWrap: "wrap", rowGap: 0.5 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {i18n.t("sites")} ({totalCount}) &amp; {i18n.t("systems")} ({totalSystems})
        </Typography>
        <SinceLastUpdate since={lastUpdateAt} />
        <Box sx={{ flex: 1 }} />
        {(sites.length > 0 || hideNoReadings) && !error && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hideNoReadings}
                onChange={(e) => setHideNoReadings(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {i18n.t("hidesitesnoreadings")}
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        )}
      </Stack>

      {showSearch && (
        <TextField
          size="small"
          placeholder={i18n.t("searchbysiteorsystem")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end">
                <CircularProgress size={18} aria-label={i18n.t("searching")} />
              </InputAdornment>
            ) : null,
          }}
        />
      )}

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          ...(searching && !error && sites.length > 0 && {
            opacity: 0.5,
            pointerEvents: "none",
            transition: "opacity 0.15s ease",
          }),
        }}
      >
        {error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" variant="outlined">
              <AlertTitle sx={{ fontWeight: 700 }}>
                {i18n.t("unabletoloadsites")}
              </AlertTitle>
              {error.response?.status
                ? i18n
                    .t("serverresponded", {
                      status: error.response.status,
                      text: error.response.statusText || "",
                    })
                    .trim()
                : error.message || i18n.t("networkerror")}
            </Alert>
          </Box>
        ) : loading && sites.length === 0 ? (
          <Stack>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={64}
                animation="wave"
                sx={{
                  borderBottom: i < 3 ? 1 : 0,
                  borderColor: "divider",
                }}
              />
            ))}
          </Stack>
        ) : sites.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {debouncedSearch.trim()
                ? i18n.t("nositesmatch", { query: debouncedSearch })
                : hideNoReadings
                  ? i18n.t("nositeswithreadings")
                  : i18n.t("nositesfound")}
            </Typography>
          </Box>
        ) : (
          orderedSites.map((site) => (
            <SiteAccordion
              key={site.csId}
              site={site}
              expanded={expandedIds.has(site.csId)}
              onToggle={toggleExpanded}
              onSystemClick={onSystemClick}
              onShowAllSystems={(s, systems) =>
                setDrawerState({ site: s, systems })
              }
              refreshKey={systemsRefreshKey ?? 0}
              siteAlerts={alerts.filter((a) => alertBelongsToSite(a, site))}
              onAlertClick={onAlertClick}
            />
          ))
        )}
      </Box>
      <SystemsDrawer
        open={drawerState !== null}
        site={
          drawerState
            ? { ...drawerState.site, systems: drawerState.systems }
            : null
        }
        onClose={() => setDrawerState(null)}
        onSystemClick={(system, site) => {
          onSystemClick?.(system, site);
          setDrawerState(null);
        }}
      />
    </Stack>
  );
}

// ── System Detail Modal ────────────────────────────────────────────────────

const STATE_LABEL_OVERRIDE_KEYS = {
  "monitoring.ok": "st_online",
  "monitoring.fault": "st_offline",
  "primary.ok": "st_connected",
  "primary.fault": "st_down",
  "cellular.ok": "st_connected",
  "cellular.fault": "st_down",
  "ac.ok": "st_connected",
  "ac.fault": "st_lost",
  "battery.ok": "st_standby",
  "battery.warn": "st_onbattery",
  "climate.ok": "st_normal",
  "climate.fault": "st_tempalarm",
  "ebutton.ok": "st_normal",
  // Covers pressed, disconnected, shorted and wiring fault; the backend message names which.
  "ebutton.fault": "st_outofrange",
};

function stateLabel(signalKey, state) {
  const override = STATE_LABEL_OVERRIDE_KEYS[`${signalKey}.${state}`];
  if (override) return i18n.t(override);
  return state ? stateText(state).toUpperCase() : i18n.t("st_unknown");
}


function SignalDetailRow({ signalKey, state, detail }) {
  const isFault = state === "fault";
  const isWarn = state === "warn";
  const isOk = state === "ok";

  let Icon = HelpOutlineRoundedIcon;
  let iconColor = "disabled";
  let labelColor = "text.secondary";
  if (isFault) {
    Icon = ErrorRoundedIcon;
    iconColor = "error";
    labelColor = "error.main";
  } else if (isWarn) {
    Icon = WarningAmberRoundedIcon;
    iconColor = "warning";
    labelColor = "warning.main";
  } else if (isOk) {
    Icon = CheckCircleRoundedIcon;
    iconColor = "success";
  }

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        py: 1.25,
        px: 1.5,
        borderLeft: isFault ? 3 : 0,
        borderColor: "error.main",
        bgcolor: (theme) =>
          isFault ? alpha(theme.palette.error.main, 0.08) : "transparent",
      }}
    >
      <Icon color={iconColor} fontSize="small" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={isFault ? 700 : 500} noWrap>
          {signalLabel(signalKey)}
        </Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {detail}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color: labelColor, letterSpacing: 0.5 }}
      >
        {stateLabel(signalKey, state)}
      </Typography>
    </Stack>
  );
}

function ModalSignalListSkeleton() {
  return (
    <Stack spacing={1}>
      {Array.from({ length: 9 }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={48} animation="wave" />
      ))}
    </Stack>
  );
}

function ModalDetailError({ error, onRetry }) {
  const status = error?.response?.status;
  const detail = status
    ? i18n
        .t("serverresponded", {
          status,
          text: error.response.statusText || "",
        })
        .trim()
    : error?.message || i18n.t("networkerror");
  return (
    <Alert
      severity="error"
      variant="outlined"
      sx={{
        "& .MuiAlert-action": { alignItems: "center", pt: 0, mt: 0 },
      }}
      action={
        onRetry && (
          <Button color="inherit" size="small" onClick={onRetry}>
            RETRY
          </Button>
        )
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>{i18n.t("unabletoloaddetail")}</AlertTitle>
      {detail}
    </Alert>
  );
}

function SystemDetailModal({
  open,
  onClose,
  loading,
  error,
  onRetry,
  system,
  site,
  lastUpdated,
  isInternal,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [diagnosticsTouched, setDiagnosticsTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setActiveTab("overview");
      setDiagnosticsTouched(false);
    }
  }, [open]);

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    if (value === "diagnostics") setDiagnosticsTouched(true);
  };

  if (!system) return null;

  const signals = system.signals;
  const hasSignals = !!signals;
  const showTabs = !!isInternal;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              {system.systemType && (
                <Chip
                  label={system.systemType}
                  color="secondary"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
              <Typography variant="h5" fontWeight={700}>
                {system.unitName}
              </Typography>
            </Stack>
            {site && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {site.csName || site.siteName}
                {site.address && ` · ${site.address}`}
              </Typography>
            )}
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                {i18n.t("lastupdatedtime", { time: lastUpdated })}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label={i18n.t("closesystemdetail")}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      {showTabs && (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={i18n.t("overview")} value="overview" />
          <Tab label={i18n.t("diagnostics")} value="diagnostics" />
        </Tabs>
      )}
      <DialogContent dividers>
        <Box hidden={activeTab !== "overview"}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SystemViewport
                systemType={system?.systemType}
                signals={signals}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "center" }}
              >
                {i18n.t("dragtorotate")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              {error ? (
                <ModalDetailError error={error} onRetry={onRetry} />
              ) : loading || !hasSignals ? (
                <ModalSignalListSkeleton />
              ) : (
                <Stack divider={<Divider />}>
                  {signalsForSystemType(system.systemType, system.unitName).map((key) => (
                    <SignalDetailRow
                      key={key}
                      signalKey={key}
                      state={signals[key]}
                      detail={system.signalDetails?.[key]}
                    />
                  ))}
                </Stack>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2, textAlign: "right" }}
              >
                {i18n.t("awarenessonly")}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        {showTabs && diagnosticsTouched && (
          <Box hidden={activeTab !== "diagnostics"}>
            <SystemDiagnostics productName={system?.unitName} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function healthDetailToModalState(data) {
  if (!data) return null;
  const signals = {};
  const signalDetails = {};
  for (const check of data.checks ?? []) {
    if (!check?.id) continue;
    signals[check.id] = check.status;
    if (check.message) signalDetails[check.id] = check.message;
  }
  return {
    system: {
      productId: data.system?.productId,
      unitName: data.system?.systemName,
      systemType: data.system?.systemType,
      signals,
      signalDetails,
    },
    site: {
      csName: data.site?.siteName,
      address: [data.site?.siteCity, data.site?.siteState]
        .filter(Boolean)
        .join(", "),
    },
    lastUpdated: data.timestamp ? timeAgo(data.timestamp) : null,
  };
}

// ── Live status indicator ──────────────────────────────────────────────────

const LIVE_STATUS = {
  reconnecting: { color: "warning.main", labelKey: "livereconnecting" },
  connecting: { color: "grey.400", labelKey: "liveconnecting" },
  disconnected: { color: "error.main", labelKey: "liveoffline" },
};

function LiveStatusIndicator({ status }) {
  if (status === "connected") return null;
  const entry = LIVE_STATUS[status] || LIVE_STATUS.disconnected;
  const cfg = { color: entry.color, label: i18n.t(entry.labelKey) };
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ ml: 0.5 }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: cfg.color,
          animation:
            status === "reconnecting"
              ? "blink 1.2s ease-in-out infinite"
              : "none",
          "@keyframes blink": {
            "0%, 100%": { opacity: 1 },
            "50%": { opacity: 0.35 },
          },
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {cfg.label}
      </Typography>
    </Stack>
  );
}

function formatElapsed(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function SinceLastUpdate({ since }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [since]);
  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  const shown = seconds <= 5 ? seconds : Math.floor(seconds / 5) * 5;
  return (
    <Typography variant="caption" color="text.secondary">
      Updated {formatElapsed(shown)} ago
    </Typography>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function HealthDashboard() {
  const { cookies } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  const [alerts, setAlerts] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(
    new Date().toLocaleTimeString(),
  );
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [alertsError, setAlertsError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const isInitialAlertsLoad = useRef(true);

  const [siteIds, setSiteIds] = useState([]);
  const [totalSiteCount, setTotalSiteCount] = useState(0);
  const [sitesRefreshKey, setSitesRefreshKey] = useState(0);
  const [systemsRefreshKey, setSystemsRefreshKey] = useState(0);
  const [lastUpdateAt, setLastUpdateAt] = useState(() => Date.now());
  const {
    status: signalRStatus,
    joinGroups,
    leaveGroups,
    subscribe,
  } = useContactDataHub();

  useEffect(() => {
    const controller = new AbortController();
    axiosPrivate
      .get(`/api/sites/${cookies.user}?page=1&pageSize=100`, {
        signal: controller.signal,
      })
      .then((response) => {
        const sites = response.data?.sites ?? [];
        const ids = sites.map((s) => s.csId).filter(Boolean);
        setSiteIds(ids);
        setTotalSiteCount(response.data?.totalCount ?? sites.length);
      })
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      });
    return () => controller.abort();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (signalRStatus !== "connected" || siteIds.length === 0) return undefined;
    joinGroups(siteIds);
    return () => {
      leaveGroups(siteIds);
    };
  }, [signalRStatus, siteIds, joinGroups, leaveGroups]);

  useEffect(() => {
    let timer;
    const handler = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setRefreshKey((k) => k + 1);
        setSitesRefreshKey((k) => k + 1);
        setSystemsRefreshKey((k) => k + 1);
      }, 400);
    };
    const unsubscribe = subscribe("SiteDataUpdated", handler);
    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [subscribe]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAlerts = async () => {
      const initial = isInitialAlertsLoad.current;
      if (initial) setLoadingAlerts(true);
      try {
        const response = await axiosPrivate.get(
          `/api/systemhealth/healthAlerts/${cookies.user}`,
          { signal: controller.signal },
        );
        if (Array.isArray(response.data)) {
          setAlerts(filterSuppressedAlerts(response.data));
          setAlertsError(null);
          setLastUpdated(new Date().toLocaleTimeString());
          setLastUpdateAt(Date.now());
          isInitialAlertsLoad.current = false;
        }
      } catch (err) {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        if (initial) setAlertsError(err);
      } finally {
        if (initial) setLoadingAlerts(false);
      }
    };

    fetchAlerts();
    return () => controller.abort();
    // eslint-disable-next-line
  }, [refreshKey]);

  const retryAlerts = () => setRefreshKey((k) => k + 1);

  const [detailModal, setDetailModal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const detailAbortRef = useRef(null);

  const fetchDetail = (productId) => {
    if (!productId) return;
    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;

    setDetailLoading(true);
    setDetailError(null);

    axiosPrivate
      .get(`/api/systemhealth/healthDetail/${cookies.user}/${productId}`, {
        signal: controller.signal,
      })
      .then((response) => {
        const next = healthDetailToModalState(response.data);
        if (!next) return;
        setDetailModal((prev) =>
          prev ? { ...prev, ...next } : next,
        );
      })
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setDetailError(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
  };

  const handleAlertClick = (alert) => {
    const productId = alert.system?.productId;
    if (!productId) return;
    setDetailModal({
      productId,
      system: {
        productId,
        unitName: alert.system?.systemName,
        systemType: alert.system?.systemType,
      },
      site: {
        csName: alert.site?.siteName,
        address: [alert.site?.siteCity, alert.site?.siteState]
          .filter(Boolean)
          .join(", "),
      },
      lastUpdated: alert.timestamp ? timeAgo(alert.timestamp) : null,
    });
    fetchDetail(productId);
  };

  const handleSystemClick = (system, site) => {
    const productId = system?.productId;
    if (!productId) return;
    setDetailModal({
      productId,
      system: {
        productId,
        unitName: system.systemName,
        systemType: system.systemType,
      },
      site: {
        csName: site?.csName,
        address: [site?.csAddress1, site?.csCity, site?.csState]
          .filter(Boolean)
          .join(", "),
      },
      lastUpdated: null,
    });
    fetchDetail(productId);
  };

  const closeDetailModal = () => {
    detailAbortRef.current?.abort();
    setDetailModal(null);
    setDetailLoading(false);
    setDetailError(null);
  };

  const retryDetail = () => {
    if (detailModal?.productId) fetchDetail(detailModal.productId);
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", lg: "80%" },
        mx: "auto",
        px: { xs: 2, sm: 3, lg: 0 },
        py: 2,
      }}
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          <LiveStatusIndicator status={signalRStatus} />
          <HealthAlerts
            alerts={alerts}
            loading={loadingAlerts}
            error={alertsError}
            totalSites={totalSiteCount}
            lastUpdated={lastUpdated}
            onAlertClick={handleAlertClick}
            onRetry={retryAlerts}
          />
        </Stack>
        <SiteLookup
          onSystemClick={handleSystemClick}
          onAlertClick={handleAlertClick}
          alerts={alerts}
          sitesRefreshKey={sitesRefreshKey}
          systemsRefreshKey={systemsRefreshKey}
          lastUpdateAt={lastUpdateAt}
        />
      </Stack>
      <SystemDetailModal
        open={detailModal !== null}
        onClose={closeDetailModal}
        loading={detailLoading}
        error={detailError}
        onRetry={retryDetail}
        system={detailModal?.system}
        site={detailModal?.site}
        lastUpdated={detailModal?.lastUpdated}
        isInternal={canViewInternalDetails(cookies.role)}
      />
    </Box>
  );
}
